/**
 * Thread Sharing Service
 * Handles sharing Chat Threads via shareable links (mirrors spaceSharingService)
 */

import { supabase, isSupabaseConfigured, getCurrentSession } from "@/lib/supabase";
import type { ThreadShare, ThreadShareAccess, ChatThread } from "@shared/schema";

export interface CreateThreadShareLinkParams {
  threadId: string;
  accessType?: "restricted" | "anyone" | "organization";
  expiresAt?: Date | null;
}

export interface ThreadShareLinkValidationResult {
  isValid: boolean;
  share: ThreadShare | null;
  thread: ChatThread | null;
  access: ThreadShareAccess | null;
  message: string;
}

class ThreadSharingService {
  private generateShareToken(): string {
    const randomBytes = new Uint8Array(24);
    crypto.getRandomValues(randomBytes);
    const base64 = btoa(String.fromCharCode(...randomBytes));
    return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "").substring(0, 32);
  }

  private async getCurrentUserId(): Promise<string> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured. Please try again later.");
    }
    const session = await getCurrentSession();
    if (!session?.user) {
      throw new Error("User not authenticated");
    }
    return session.user.id;
  }

  private mapToThreadShare(item: any): ThreadShare {
    return {
      id: item.id,
      threadId: item.thread_id,
      createdByUserId: item.created_by_user_id,
      shareToken: item.share_token,
      accessType: item.access_type,
      expiresAt: item.expires_at,
      isActive: item.is_active === "true",
      createdAt: item.created_at,
      updatedAt: item.updated_at,
    };
  }

  private mapToThreadShareAccess(item: any): ThreadShareAccess {
    return {
      id: item.id,
      shareId: item.share_id,
      userId: item.user_id,
      status: item.status,
      accessedAt: item.accessed_at,
      respondedAt: item.responded_at,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
    };
  }

  private mapRowToThread(thread: any): ChatThread {
    const isArchived =
      thread?.status === "archived" ||
      thread?.isArchived === "true" ||
      thread?.is_archived === "true";
    const isLocked =
      thread?.isLocked === "true" ||
      thread?.is_locked === "true" ||
      !!(thread?.lockedAt ?? thread?.locked_at);
    return {
      id: thread.id,
      userId: thread.userId ?? thread.user_id,
      title: thread.title,
      topic: thread.topic,
      topicId: thread.topicId ?? thread.topic_id,
      tags: thread.tags ?? [],
      messages: thread.messages ?? [],
      linkedSpaceIds: thread.linkedSpaceIds ?? thread.linked_space_ids ?? [],
      agentId: thread.agentId ?? thread.agent_id,
      contextMode: thread.contextMode ?? thread.context_mode ?? "MAX",
      status: thread.status ?? "active",
      isArchived: isArchived ? "true" : "false",
      archivedAt: thread.archivedAt ?? thread.archived_at,
      archiveSummaryDocId: thread.archiveSummaryDocId ?? thread.archive_summary_doc_id,
      isLocked: isLocked ? "true" : "false",
      lockedAt: thread.lockedAt ?? thread.locked_at ?? null,
      createdAt: thread.createdAt ?? thread.created_at,
      updatedAt: thread.updatedAt ?? thread.updated_at,
    } as ChatThread;
  }

  async createShareLink(params: CreateThreadShareLinkParams): Promise<ThreadShare> {
    const userId = await this.getCurrentUserId();

    const { data: thread, error: threadError } = await supabase
      .from("chat-threads")
      .select("id, title")
      .eq("id", params.threadId)
      .eq("userId", userId)
      .single();

    if (threadError || !thread) {
      throw new Error("Thread not found or you don't have permission to share it");
    }

    const { data: existingShare } = await supabase
      .from("thread_shares")
      .select("id, thread_id, created_by_user_id, share_token, access_type, expires_at, is_active, created_at, updated_at")
      .eq("thread_id", params.threadId)
      .eq("created_by_user_id", userId)
      .eq("is_active", "true")
      .single();

    if (existingShare) {
      const updateData: any = { updated_at: new Date().toISOString() };
      if (params.accessType) updateData.access_type = params.accessType;
      if (params.expiresAt !== undefined) updateData.expires_at = params.expiresAt?.toISOString() || null;

      const { data: updatedShare, error: updateError } = await supabase
        .from("thread_shares")
        .update(updateData)
        .eq("id", existingShare.id)
        .select()
        .single();

      if (updateError) throw new Error(`Failed to update share link: ${updateError.message}`);
      return this.mapToThreadShare(updatedShare);
    }

    const shareToken = this.generateShareToken();
    const shareData: any = {
      thread_id: params.threadId,
      created_by_user_id: userId,
      share_token: shareToken,
      access_type: params.accessType || "restricted",
      expires_at: params.expiresAt?.toISOString() || null,
      is_active: "true",
    };

    const { data: newShare, error: createError } = await supabase
      .from("thread_shares")
      .insert(shareData)
      .select()
      .single();

    if (createError) throw new Error(`Failed to create share link: ${createError.message}`);
    return this.mapToThreadShare(newShare);
  }

  async validateShareLink(token: string): Promise<ThreadShareLinkValidationResult> {
    const { data: share, error: shareError } = await supabase
      .from("thread_shares")
      .select("id, thread_id, created_by_user_id, share_token, access_type, expires_at, is_active, created_at, updated_at")
      .eq("share_token", token)
      .eq("is_active", "true")
      .single();

    if (shareError || !share) {
      return {
        isValid: false,
        share: null,
        thread: null,
        access: null,
        message: "Link not valid or has been deactivated",
      };
    }

    if (share.expires_at) {
      const expiresAt = new Date(share.expires_at);
      if (expiresAt < new Date()) {
        return {
          isValid: false,
          share: this.mapToThreadShare(share),
          thread: null,
          access: null,
          message: "This share link has expired",
        };
      }
    }

    const { data: threadRow, error: threadError } = await supabase
      .from("chat-threads")
      .select("id, userId, title, topic, topicId, tags, messages, linkedSpaceIds, agentId, contextMode, status, isArchived, archivedAt, archiveSummaryDocId, is_locked, locked_at, createdAt, updatedAt")
      .eq("id", share.thread_id)
      .single();

    if (threadError || !threadRow) {
      return {
        isValid: false,
        share: this.mapToThreadShare(share),
        thread: null,
        access: null,
        message: "Thread not found or has been deleted",
      };
    }

    let access: ThreadShareAccess | null = null;
    try {
      const userId = await this.getCurrentUserId();
      const { data: accessRecord } = await supabase
        .from("thread_share_access")
        .select("id, share_id, user_id, status, accessed_at, responded_at, created_at, updated_at")
        .eq("share_id", share.id)
        .eq("user_id", userId)
        .single();

      if (accessRecord) access = this.mapToThreadShareAccess(accessRecord);
    } catch {
      // User not authenticated
    }

    return {
      isValid: true,
      share: this.mapToThreadShare(share),
      thread: this.mapRowToThread(threadRow),
      access,
      message: "Link is valid",
    };
  }

  async acceptShareInvitation(token: string): Promise<ThreadShareAccess> {
    const userId = await this.getCurrentUserId();

    const { data: share, error: shareError } = await supabase
      .from("thread_shares")
      .select("id, thread_id, created_by_user_id, share_token, access_type, expires_at, is_active, created_at, updated_at")
      .eq("share_token", token)
      .eq("is_active", "true")
      .single();

    if (shareError || !share) throw new Error("Share link not found or invalid");

    const { data: existingAccess } = await supabase
      .from("thread_share_access")
      .select("id, share_id, user_id, status, accessed_at, responded_at, created_at, updated_at")
      .eq("share_id", share.id)
      .eq("user_id", userId)
      .single();

    if (existingAccess) {
      if (existingAccess.status !== "accepted") {
        const { data: updatedAccess, error: updateError } = await supabase
          .from("thread_share_access")
          .update({
            status: "accepted",
            responded_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq("id", existingAccess.id)
          .select()
          .single();
        if (updateError) throw new Error(`Failed to accept invitation: ${updateError.message}`);
        return this.mapToThreadShareAccess(updatedAccess);
      }
      return this.mapToThreadShareAccess(existingAccess);
    }

    const accessData: any = {
      share_id: share.id,
      user_id: userId,
      status: "accepted",
      accessed_at: new Date().toISOString(),
      responded_at: new Date().toISOString(),
    };

    const { data: newAccess, error: createError } = await supabase
      .from("thread_share_access")
      .insert(accessData)
      .select()
      .single();

    if (createError) throw new Error(`Failed to accept invitation: ${createError.message}`);
    return this.mapToThreadShareAccess(newAccess);
  }

  async rejectShareInvitation(token: string): Promise<ThreadShareAccess> {
    const userId = await this.getCurrentUserId();

    const { data: share, error: shareError } = await supabase
      .from("thread_shares")
      .select("id, thread_id, created_by_user_id, share_token, access_type, expires_at, is_active, created_at, updated_at")
      .eq("share_token", token)
      .eq("is_active", "true")
      .single();

    if (shareError || !share) throw new Error("Share link not found or invalid");

    const { data: existingAccess } = await supabase
      .from("thread_share_access")
      .select("id, share_id, user_id, status, accessed_at, responded_at, created_at, updated_at")
      .eq("share_id", share.id)
      .eq("user_id", userId)
      .single();

    if (existingAccess) {
      const { data: updatedAccess, error: updateError } = await supabase
        .from("thread_share_access")
        .update({
          status: "rejected",
          responded_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", existingAccess.id)
        .select()
        .single();
      if (updateError) throw new Error(`Failed to reject invitation: ${updateError.message}`);
      return this.mapToThreadShareAccess(updatedAccess);
    }

    const accessData: any = {
      share_id: share.id,
      user_id: userId,
      status: "rejected",
      accessed_at: new Date().toISOString(),
      responded_at: new Date().toISOString(),
    };

    const { data: newAccess, error: createError } = await supabase
      .from("thread_share_access")
      .insert(accessData)
      .select()
      .single();

    if (createError) throw new Error(`Failed to reject invitation: ${createError.message}`);
    return this.mapToThreadShareAccess(newAccess);
  }

  async revokeShareLink(threadId: string): Promise<boolean> {
    const userId = await this.getCurrentUserId();

    const { error } = await supabase
      .from("thread_shares")
      .update({
        is_active: "false",
        updated_at: new Date().toISOString(),
      })
      .eq("thread_id", threadId)
      .eq("created_by_user_id", userId);

    if (error) throw new Error(`Failed to revoke share link: ${error.message}`);
    return true;
  }

  async getShareLink(threadId: string): Promise<ThreadShare | null> {
    const userId = await this.getCurrentUserId();

    const { data: share, error } = await supabase
      .from("thread_shares")
      .select("id, thread_id, created_by_user_id, share_token, access_type, expires_at, is_active, created_at, updated_at")
      .eq("thread_id", threadId)
      .eq("created_by_user_id", userId)
      .eq("is_active", "true")
      .single();

    if (error || !share) return null;
    return this.mapToThreadShare(share);
  }
}

export const threadSharingService = new ThreadSharingService();
